import React, { useState, useCallback, useMemo } from 'react';
import { RotateCcw, ChevronDown, ChevronRight, Lock, Unlock } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Slider } from '../components/ui/slider';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import * as THREE from 'three';

// Convert quaternion to euler angles (degrees)
function quaternionToEulerDegrees(q) {
  const euler = new THREE.Euler();
  const quat = new THREE.Quaternion(q[0], q[1], q[2], q[3]);
  euler.setFromQuaternion(quat, 'XYZ');
  return {
    x: THREE.MathUtils.radToDeg(euler.x),
    y: THREE.MathUtils.radToDeg(euler.y),
    z: THREE.MathUtils.radToDeg(euler.z)
  };
}

// Convert euler angles (degrees) to quaternion
function eulerDegreesToQuaternion(x, y, z) {
  const euler = new THREE.Euler(
    THREE.MathUtils.degToRad(x),
    THREE.MathUtils.degToRad(y),
    THREE.MathUtils.degToRad(z),
    'XYZ'
  );
  const quat = new THREE.Quaternion();
  quat.setFromEuler(euler);
  return [quat.x, quat.y, quat.z, quat.w];
}

export default function BoneInspector({
  selectedBone,
  boneTransform,
  onRotationChange,
  onReset,
  bones = []
}) {
  const [localRotation, setLocalRotation] = useState({ x: 0, y: 0, z: 0 });
  const [isExpanded, setIsExpanded] = useState(true);
  const [linkedAxes, setLinkedAxes] = useState(false);
  
  // Derive rotation from bone transform prop (without using effect to set state)
  const derivedRotation = useMemo(() => {
    if (boneTransform?.rotation) {
      const euler = quaternionToEulerDegrees(boneTransform.rotation);
      return {
        x: Math.round(euler.x * 100) / 100,
        y: Math.round(euler.y * 100) / 100,
        z: Math.round(euler.z * 100) / 100
      };
    }
    return { x: 0, y: 0, z: 0 };
  }, [boneTransform]);
  
  // Use local rotation if user is editing, otherwise use derived rotation
  const displayRotation = localRotation.x !== 0 || localRotation.y !== 0 || localRotation.z !== 0
    ? localRotation 
    : derivedRotation;
  
  // Handle rotation change
  const handleRotationChange = useCallback((axis, value) => {
    const currentRotation = displayRotation;
    const newRotation = { ...currentRotation, [axis]: value };
    
    // If axes are linked, apply same delta to other axes
    if (linkedAxes) {
      const delta = value - currentRotation[axis];
      ['x', 'y', 'z'].forEach(a => {
        if (a !== axis) {
          newRotation[a] = currentRotation[a] + delta;
        }
      });
    }
    
    setLocalRotation(newRotation);
    
    // Convert to quaternion and notify parent
    const quaternion = eulerDegreesToQuaternion(newRotation.x, newRotation.y, newRotation.z);
    if (onRotationChange) {
      onRotationChange(selectedBone, { rotation: quaternion });
    }
  }, [displayRotation, linkedAxes, selectedBone, onRotationChange]);
  
  // Handle reset
  const handleReset = useCallback(() => {
    setLocalRotation({ x: 0, y: 0, z: 0 });
    if (onReset) {
      onReset(selectedBone);
    }
  }, [selectedBone, onReset]);
  
  // Handle input change
  const handleInputChange = useCallback((axis, inputValue) => {
    const value = parseFloat(inputValue);
    if (!isNaN(value)) {
      handleRotationChange(axis, Math.max(-180, Math.min(180, value)));
    }
  }, [handleRotationChange]);
  
  if (!selectedBone) {
    return (
      <div className="bg-slate-800 border-l border-slate-700 w-64 p-4">
        <h3 className="text-sm font-semibold text-slate-400 mb-4">Bone Inspector</h3>
        <p className="text-xs text-slate-500 text-center py-8">
          Select a bone to adjust its rotation
        </p>
      </div>
    );
  }
  
  return (
    <div className="bg-slate-800 border-l border-slate-700 w-64 flex flex-col" data-testid="bone-inspector">
      {/* Header */}
      <div className="p-3 border-b border-slate-700">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold text-white">Bone Inspector</h3>
          <Button
            variant="ghost"
            size="sm"
            className="h-6 w-6 p-0"
            onClick={() => setIsExpanded(!isExpanded)}
          >
            {isExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
          </Button>
        </div>
        <p className="text-xs text-purple-400 mt-1 truncate">{selectedBone}</p>
      </div>
      
      {isExpanded && (
        <div className="flex-1 overflow-auto p-3 space-y-4">
          {/* Rotation Controls */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <Label className="text-xs text-slate-400">Rotation (degrees)</Label>
              <div className="flex items-center gap-1">
                <Button
                  variant="ghost"
                  size="sm"
                  className={`h-6 w-6 p-0 ${linkedAxes ? 'text-purple-400' : 'text-slate-500'}`}
                  onClick={() => setLinkedAxes(!linkedAxes)}
                  title={linkedAxes ? 'Unlink axes' : 'Link axes'}
                >
                  {linkedAxes ? <Lock size={12} /> : <Unlock size={12} />}
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-6 w-6 p-0 text-slate-400 hover:text-white"
                  onClick={handleReset}
                  title="Reset rotation"
                >
                  <RotateCcw size={12} />
                </Button>
              </div>
            </div>
            
            {/* X Rotation */}
            <div className="space-y-1">
              <div className="flex items-center justify-between">
                <Label className="text-xs text-red-400 w-6">X</Label>
                <Input
                  type="number"
                  value={displayRotation.x.toFixed(1)}
                  onChange={(e) => handleInputChange('x', e.target.value)}
                  className="h-6 w-20 text-xs bg-slate-900 border-slate-700 text-right"
                  step="1"
                />
              </div>
              <Slider
                value={[displayRotation.x]}
                onValueChange={([v]) => handleRotationChange('x', v)}
                min={-180}
                max={180}
                step={1}
                className="[&_[role=slider]]:bg-red-500 [&_[role=slider]]:border-red-500"
              />
            </div>
            
            {/* Y Rotation */}
            <div className="space-y-1">
              <div className="flex items-center justify-between">
                <Label className="text-xs text-green-400 w-6">Y</Label>
                <Input
                  type="number"
                  value={displayRotation.y.toFixed(1)}
                  onChange={(e) => handleInputChange('y', e.target.value)}
                  className="h-6 w-20 text-xs bg-slate-900 border-slate-700 text-right"
                  step="1"
                />
              </div>
              <Slider
                value={[displayRotation.y]}
                onValueChange={([v]) => handleRotationChange('y', v)}
                min={-180}
                max={180}
                step={1}
                className="[&_[role=slider]]:bg-green-500 [&_[role=slider]]:border-green-500"
              />
            </div>
            
            {/* Z Rotation */}
            <div className="space-y-1">
              <div className="flex items-center justify-between">
                <Label className="text-xs text-blue-400 w-6">Z</Label>
                <Input
                  type="number"
                  value={displayRotation.z.toFixed(1)}
                  onChange={(e) => handleInputChange('z', e.target.value)}
                  className="h-6 w-20 text-xs bg-slate-900 border-slate-700 text-right"
                  step="1"
                />
              </div>
              <Slider
                value={[displayRotation.z]}
                onValueChange={([v]) => handleRotationChange('z', v)}
                min={-180}
                max={180}
                step={1}
                className="[&_[role=slider]]:bg-blue-500 [&_[role=slider]]:border-blue-500"
              />
            </div>
          </div>
          
          {/* Quick Presets */}
          <div className="space-y-2 pt-2 border-t border-slate-700">
            <Label className="text-xs text-slate-400">Quick Presets</Label>
            <div className="grid grid-cols-2 gap-1">
              <Button
                variant="outline"
                size="sm"
                className="h-7 text-xs"
                onClick={() => {
                  setLocalRotation({ x: 0, y: 0, z: 0 });
                  const q = eulerDegreesToQuaternion(0, 0, 0);
                  onRotationChange?.(selectedBone, { rotation: q });
                }}
              >
                Reset
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="h-7 text-xs"
                onClick={() => {
                  setLocalRotation({ x: 45, y: 0, z: 0 });
                  const q = eulerDegreesToQuaternion(45, 0, 0);
                  onRotationChange?.(selectedBone, { rotation: q });
                }}
              >
                +45° X
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="h-7 text-xs"
                onClick={() => {
                  setLocalRotation({ x: -45, y: 0, z: 0 });
                  const q = eulerDegreesToQuaternion(-45, 0, 0);
                  onRotationChange?.(selectedBone, { rotation: q });
                }}
              >
                -45° X
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="h-7 text-xs"
                onClick={() => {
                  setLocalRotation({ x: 0, y: 90, z: 0 });
                  const q = eulerDegreesToQuaternion(0, 90, 0);
                  onRotationChange?.(selectedBone, { rotation: q });
                }}
              >
                +90° Y
              </Button>
            </div>
          </div>
          
          {/* Current Quaternion Display (collapsed by default) */}
          <details className="text-xs text-slate-500 pt-2 border-t border-slate-700">
            <summary className="cursor-pointer hover:text-slate-400">Quaternion (raw)</summary>
            <div className="mt-2 font-mono bg-slate-900 p-2 rounded text-[10px]">
              {boneTransform?.rotation ? (
                <>
                  <div>x: {boneTransform.rotation[0].toFixed(4)}</div>
                  <div>y: {boneTransform.rotation[1].toFixed(4)}</div>
                  <div>z: {boneTransform.rotation[2].toFixed(4)}</div>
                  <div>w: {boneTransform.rotation[3].toFixed(4)}</div>
                </>
              ) : (
                <div>No transform data</div>
              )}
            </div>
          </details>
        </div>
      )}
    </div>
  );
}
